import { useEffect, useState } from 'react';
import { X, Search, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';

// Last game (Match queue, owner 2026-10-01): a player who has already played
// says "this is my last one". On court → this game is their last. In the
// queue → their next match moves to the front. Done after it (default on).
// Players with no game yet are not listed — they have no "last game" yet.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === 'string' ? d : null))) || fallback;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const C = {
  surface: '#0E0708', line: '#2E161B', lineStrong: '#3A1A20', amber: '#F59E0B',
  sheet: 'linear-gradient(180deg, #170B0E 0%, #100809 100%)',
};

export default function LastGameModal({ sessionId, divisions = [], requirePin, clearPin, onClose, onDone }) {
  const [did, setDid] = useState(divisions[0]?.id);
  const [opts, setOpts] = useState(null);
  const [err, setErr] = useState('');
  const [who, setWho] = useState(null);
  const [doneAfter, setDoneAfter] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setOpts(null); setWho(null);
    sportsApi.get(`/sports/open-play/${sessionId}/custom-match/options`, { params: { division_id: did } })
      .then(r => { if (live) { setOpts(r.data); setErr(''); } })
      .catch(e => { if (live) setErr(errMsg(e, 'Could not load the players')); });
    return () => { live = false; };
  }, [sessionId, did]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const all = (opts?.players || []).filter(p => p.status === 'free' || p.status === 'on_court');
  const played = all.filter(p => p.games > 0);
  const fresh = all.filter(p => !p.games);
  const list = played.filter(p => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const p = played.find(x => x.id === who);
  const inQueue = p && p.status === 'free' && p.next_position;
  const canGo = p && (p.status === 'on_court' || inQueue);

  const submit = async () => {
    const pin = await requirePin();
    if (pin === null) return;
    setBusy(true);
    try {
      const r = await sportsApi.put(`/sports/open-play/${sessionId}/players/${who}/last-game`, { pin, done_after: doneAfter });
      toast.success(r.data?.started ? `${p.name}'s match started on a free court${doneAfter ? ' — done after it' : ''}` : r.data?.message);
      onDone();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      setErr(errMsg(e, 'Could not move their match'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" data-testid="last-game-modal"
      style={{ background: 'rgba(0,0,0,0.78)' }} onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="last-title"
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-4 sm:p-5"
        style={{ background: C.sheet, border: `1px solid ${C.lineStrong}`,
                 boxShadow: '0 30px 80px #000, 0 0 0 1px rgba(230,57,70,0.12), 0 0 60px -20px rgba(230,57,70,0.35)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 id="last-title" className="text-lg font-bold uppercase inline-flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>
            <Flag className="h-4 w-4" style={{ color: C.amber }} /> Last game
          </h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm mt-1 mb-3" style={{ color: '#A3A3A3' }}>
          A player says they're leaving after their next game. Bump that game to the front so they get it.
          <span style={{ color: '#777' }}> Only players who have already played are listed{fresh.length ? ` (not ${fresh.map(x => x.name).join(', ')} — no game yet)` : ''}.</span>
        </p>
        {divisions.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {divisions.map(d => (
              <button key={d.id} type="button" onClick={() => setDid(d.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: did === d.id ? '#F59E0B14' : C.surface, color: did === d.id ? '#FCD34D' : '#BDBDBD', border: `1px solid ${did === d.id ? '#F59E0B66' : C.line}` }}>
                {d.name}
              </button>
            ))}
          </div>
        )}
        {!opts && !err && <p className="text-sm py-6 text-center" style={{ color: '#737373' }}>Loading players…</p>}
        {opts && (
          <>
            <div className="relative mb-2.5">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] pointer-events-none" style={{ color: C.amber }} />
              <input data-testid="last-search" value={q} onChange={e => setQ(e.target.value)} autoComplete="off" placeholder="Search players…"
                className="w-full rounded-xl pl-11 pr-3.5 py-3 text-[15px] font-semibold outline-none placeholder:text-[#B5B5B5] placeholder:font-medium"
                style={{ minHeight: 48, background: '#1C1013', color: '#fff', border: '1.5px solid #F59E0B70',
                         boxShadow: '0 0 0 4px #F59E0B10, 0 6px 18px -8px rgba(245,158,11,0.45)' }} />
            </div>
            <div className="rounded-xl overflow-hidden max-h-72 overflow-y-auto" style={{ border: `1px solid ${C.line}` }}>
              {list.length ? list.map((x, i) => {
                const on = who === x.id;
                const where = x.status === 'on_court' ? `playing now${x.court_name ? ` on ${x.court_name}` : ''} — this game can be their last`
                  : x.next_position ? `next game #${x.next_position}` : 'not in the queue';
                return (
                  <button key={x.id} type="button" data-testid={`last-pick-${x.id}`} onClick={() => setWho(x.id)}
                    className="w-full text-left px-3 py-2.5"
                    style={{ background: on ? '#F59E0B12' : C.surface, boxShadow: on ? `inset 3px 0 0 ${C.amber}` : 'none', borderTop: i ? `1px solid ${C.line}` : 0 }}>
                    <b className="text-sm" style={{ color: '#E5E5E5' }}>{x.name}</b>
                    <span className="block text-xs mt-0.5" style={{ color: '#8A8A8A' }}>{plural(x.games, 'game', 'games')} · {where}</span>
                  </button>
                );
              }) : <p className="px-3 py-2.5 text-sm" style={{ background: C.surface, color: '#777' }}>{q.trim() ? `No player matches “${q.trim()}”` : 'Nobody has played yet.'}</p>}
            </div>
            {p && (
              <button type="button" data-testid="last-done-after" onClick={() => setDoneAfter(!doneAfter)}
                className="w-full text-left rounded-xl px-3 py-2.5 mt-3"
                style={{ background: doneAfter ? '#F59E0B0D' : C.surface, border: `1px solid ${doneAfter ? C.amber : C.line}` }}>
                <b className="block text-[13.5px]" style={{ color: '#fff' }}>{doneAfter ? '☑' : '☐'} Mark {p.name} Done after that game</b>
                <span className="text-xs" style={{ color: '#8A8A8A' }}>Their later queued seats go to substitutes.</span>
              </button>
            )}
            {p && !canGo && (
              <p className="text-xs mt-3 px-3 py-2.5 rounded-lg" style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
                {p.name} isn't in the queue. Use <b>Make a match</b> to give them a last game.
              </p>
            )}
          </>
        )}
        {err && <p data-testid="last-error" className="text-xs mt-3 px-3 py-2 rounded-lg" style={{ color: '#FCA5A5', background: '#EF44441A', border: '1px solid #EF444440' }}>{err}</p>}
        <div className="flex gap-2 justify-end sticky bottom-0 pt-3 mt-3" style={{ background: '#100809' }}>
          <button type="button" data-testid="last-cancel" onClick={onClose} disabled={busy}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={{ color: '#E5E5E5', border: `1px solid ${C.lineStrong}`, background: '#120A0C' }}>
            Cancel
          </button>
          <button type="button" data-testid="last-go" disabled={busy || !canGo} onClick={submit}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-black uppercase"
            style={{ background: C.amber, color: '#111', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5,
                     opacity: busy || !canGo ? 0.45 : 1, boxShadow: busy || !canGo ? 'none' : '0 6px 22px -6px rgba(245,158,11,0.75)' }}>
            {busy ? 'Moving…' : !p ? 'Pick a player' : p.status === 'on_court' ? 'This game is their last'
              : p.next_position === 1 ? 'Keep at #1' : 'Move their match to #1'}
          </button>
        </div>
      </div>
    </div>
  );
}
